
	import {Component, Vue, Watch} from 'vue-property-decorator';
	import { AxiosStatic } from 'axios';
	import IResource from "@/IResource";
	import IReservation from '@/IReservation';

	interface ITimeOption {
		value: number;
		label: string;
		enabled: boolean;
	}

	@Component({
		components: {},
	})
	export default class EditReservation extends Vue {
		public $http!: AxiosStatic;

		public isLoading: boolean = false;
		public isDeleting: boolean = false;
		public isDeleted: boolean = false;
		public isSaved: boolean = false;
		public validationActive: boolean = false;
		public availability: { [key: string]: [number, number][] } = {};
		public name: string = '';
		public email: string = '';
		public fromtime: number = 0;
		public totime: number = 0;
		public errors = {
			name: false,
			email: false,
			fromtime: false,
			totime: false,
			save: false,
			delete: false,
			customvalues: {} as { [id: number]: boolean }
		};

		public customvalues: { [id: number]: any } = {};

		public startOptions: {[key: number]: ITimeOption} = {};
		public endOptions: {[key: number]: ITimeOption} = {};

		public async mounted() {
			this.name = this.reservation.name;
			this.email = this.reservation.email;

			let startOfDay = new Date(this.reservation.start);
			startOfDay.setHours(0,0,0,0);
			let startMinutesOfDay = startOfDay.getTime() / 60 / 1000;
			this.fromtime = +(new Date(this.reservation.start)) / 60 / 1000 - startMinutesOfDay;
			this.totime = +(new Date(this.reservation.end)) / 60 / 1000 - startMinutesOfDay;

			this.reservation.customvalues.forEach(customvalue => {
				let field = this.resource.customfields.find(field => field.id === customvalue.id);
				if (field) {
					if (field.type === 'checkbox') {
						this.customvalues[customvalue.id] = customvalue.value === '1';
					} else {
						this.customvalues[customvalue.id] = customvalue.value;
					}
				} else {
					this.customvalues[customvalue.id] = customvalue.value;
				}

			})
			if (this.isEditable) {
				await this.loadAvailability();
				this.buildTimeOptions();
			}

			this.$gtag.pageview( {page_path: '/edit-reservation' });
		}

		get isEditable(): boolean {
			return this.resource.auto_confirm === 1 &&
				this.resource.reservation_customer_editable === 1 &&
				this.resource.type === 'singleday' &&
				this.resource.auto_confirm === 1 &&
				new Date(this.reservation.end) >= new Date() &&
				this.reservation.status !== 'deleted';
		}

		get step(): string {
			return this.$store.state.step;
		}

		get resource(): IResource {
			return this.$store.state.resource;
		}

		get reservation(): IReservation {
			return this.$store.state.reservation;
		}

		public async loadAvailability() {
			let rangeStart = new Date(this.reservation.start);
			let rangeEnd = new Date(this.reservation.end);
			rangeEnd.setDate(rangeEnd.getDate() + 1);

			let url = `/public/resource/${this.$store.state.resourcehash}/availability?excluded_customeredithash=${this.$store.state.customeredithash}&start=${rangeStart.toISOString().substring(0,10)}&end=${rangeEnd.toISOString().substring(0,10)}`;
			let response = await this.$http.get(url);
			this.availability = response.data;
		}

		get dateKey() {
			let d = new Date(this.reservation.start);
			return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
		}

		get filteredEndOptions(): {[key: number]: ITimeOption} {
			let options: {[key: number]: ITimeOption} = {};
			let disabledOptionFound = false;
			Object.keys(this.endOptions).forEach(keyStr => {
				const key: number = parseInt(keyStr, 10);
				if (this.endOptions[key].value > this.fromtime) {
					if (!disabledOptionFound) {
						options[key] = JSON.parse(JSON.stringify(this.endOptions[key]));
						if (!options[key].enabled) {
							options[key].enabled = true; // make starttime of next reservation selectable as endtime
						}
					}
					if (!this.endOptions[key].enabled) {
						disabledOptionFound = true;
					}
				}
			});

			return options;
		}

		public buildTimeOptions() {
			if (this.resource !== null) {
				let start = 0;
				if (this.resource.starttime !== null) {
					start = this.resource.starttime * 60;
				}
				let end = 0;
				if (this.resource.endtime !== null) {
					end = this.resource.endtime * 60;
				}
				let step = this.resource.slot_size;

				let minutes;
				for (minutes = start; minutes <= end; minutes += step) {
					let mins: number|string = minutes % 60;
					if (mins < 10) {
						mins = "0" + mins;
					}
					let hours: number|string = Math.floor(minutes / 60);
					if (hours < 10) {
						hours = "0" + hours;
					}
					let data: ITimeOption = {
						value: minutes,
						label: hours + ':' + mins,
						enabled: true
					};
					if (minutes < end) {
						this.$set(this.startOptions, minutes, data);
					}
					if (minutes > start) {
						this.$set(this.endOptions, minutes, data);
					}
				}

				if (this.availability && this.availability[this.dateKey]) {
					this.availability[this.dateKey].forEach(reservation => {
						let minutes;
						for (minutes = reservation[0]; minutes < reservation[1]; minutes++) {
							if (this.startOptions[minutes]) {
								this.startOptions[minutes].enabled = false;
							}
							if (this.endOptions[minutes]) {
								this.endOptions[minutes].enabled = false;
							}
						}
					})
				}
			}
		}

		public checkForm() {
			let isValid: boolean = true;
			this.errors.name = false;
			this.errors.email = false;
			this.errors.fromtime = false;
			this.errors.totime = false;
			this.errors.customvalues = {};
			if (!this.name) {
				this.errors.name = true;
				isValid = false;
			}
			if (!this.checkEmail()) {
				this.errors.email = true;
				isValid = false;
			}

			if (this.fromtime === 0) {
				this.errors.fromtime = true;
				isValid = false;
			}

			if (this.totime === 0 || !(this.totime in this.filteredEndOptions)) {
				this.errors.totime = true;
				isValid = false;
			}

			// validate custom fields
			if (this.resource.customfields) {
				this.resource.customfields.forEach((field: any) => {
					if (field.required) {
						if (field.type === 'checkbox') {
							if (typeof this.customvalues[field.id] === 'undefined' || !this.customvalues[field.id]) {
								isValid = false;
								this.$set(this.errors.customvalues, field.id, true);
							}
						} else {
							if (typeof this.customvalues[field.id] === 'undefined' || this.customvalues[field.id].length === 0) {
								isValid = false;
								this.$set(this.errors.customvalues, field.id, true);
							}
						}
					}
				});
			}
			return isValid;
		}

		public checkEmail() {
			let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			return re.test(this.email.toLowerCase());
		}

		@Watch('name')
		@Watch('email')
		@Watch('customvalues', {deep: true})
		public validateOnInput() {
			if (this.validationActive) {
				this.checkForm();
			}
		}

		public async saveReservation() {
			let start = new Date(this.reservation.start);
			start.setHours(Math.floor(this.fromtime / 60), Math.floor(this.fromtime % 60));
			let end = new Date(this.reservation.end);
			end.setHours(Math.floor(this.totime / 60), Math.floor(this.totime % 60));
			let data = {
				name: this.$store.state.name,
				email: this.$store.state.email,
				start: this.formatDateAsIso(start),
				end: this.formatDateAsIso(end),
				customvalues: this.customvalues
			};

			this.isLoading = true;
			this.errors.save = false;
			this.isSaved = false;
			this.isDeleted = false;
			this.errors.delete = false;
			try {
				await this.$http.put(`/public/resource/${this.$store.state.resourcehash}/reservation/${this.$store.state.customeredithash}`, data);
				this.isSaved = true;
				this.$store.commit('SET_STEP', 'view');
			} catch (e) {
				this.errors.save = true;
			} finally {
				this.isLoading = false;
			}
			return true;
		}

		public formatDateAsIso(date: Date) {
			return `${date.getFullYear()}-${this.padZero(date.getMonth() + 1)}-${this.padZero(date.getDate())} ${this.padZero(date.getHours())}:${this.padZero(date.getMinutes())}:00`;
		}

		public padZero(num: number) {
			return num < 10 ? '0' + num : num;
		}

		public async checkAndSaveData() {
			this.validationActive = true;
			if(this.checkForm()) {
				this.$store.commit('SET_NAME', this.name);
				this.$store.commit('SET_MAIL', this.email);
				await this.saveReservation();
			}
		}

		public async deleteReservation() {
			if (confirm('Soll die Reservation wirklich storniert werden?')) {
				this.isLoading = true;
				this.isDeleting = true;
				this.errors.save = false;
				this.isSaved = false;
				this.isDeleted = false;
				this.errors.delete = false;
				try {
					await this.$http.delete(`/public/resource/${this.$store.state.resourcehash}/reservation/${this.$store.state.customeredithash}`);
					this.isDeleted = true;
				} catch (error) {
					this.errors.delete = true;
				} finally {
					this.isLoading = false;
					this.isDeleting = false;
				}
			}
		}
	}
